import React, { useState } from 'react';
import { EventSection } from '../data/upcomingEvents';

interface EventCalendarProps {
    events: EventSection[];
    selectedDate: string | null;
    onSelectDate: (date: string | null) => void;
}

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

function toISO(year: number, month: number, day: number): string {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function dateHasEvent(isoDate: string, events: EventSection[]): boolean {
    return events.some((e) => {
        if (e.endDate) {
            return isoDate >= e.date && isoDate <= e.endDate;
        }
        return e.date === isoDate;
    });
}

const EventCalendar: React.FC<EventCalendarProps> = ({ events, selectedDate, onSelectDate }) => {
    const today = new Date();
    const [viewYear, setViewYear] = useState(() => {
        // Start on the month of the first upcoming event, or today if none
        const upcoming = events.find((e) => e.date >= toISO(today.getFullYear(), today.getMonth(), today.getDate()));
        if (upcoming) {
            return parseInt(upcoming.date.slice(0, 4));
        }
        return today.getFullYear();
    });
    const [viewMonth, setViewMonth] = useState(() => {
        const upcoming = events.find((e) => e.date >= toISO(today.getFullYear(), today.getMonth(), today.getDate()));
        if (upcoming) {
            return parseInt(upcoming.date.slice(5, 7)) - 1;
        }
        return today.getMonth();
    });

    const todayISO = toISO(today.getFullYear(), today.getMonth(), today.getDate());

    function prevMonth() {
        if (viewMonth === 0) {
            setViewMonth(11);
            setViewYear((y) => y - 1);
        } else {
            setViewMonth((m) => m - 1);
        }
    }

    function nextMonth() {
        if (viewMonth === 11) {
            setViewMonth(0);
            setViewYear((y) => y + 1);
        } else {
            setViewMonth((m) => m + 1);
        }
    }

    // Build the 6-week grid (Mon-first)
    const firstDay = new Date(viewYear, viewMonth, 1);
    // getDay(): 0=Sun,1=Mon,...,6=Sat → shift so Mon=0
    const startOffset = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const cells: { iso: string; day: number; currentMonth: boolean }[] = [];

    // Previous month tail
    for (let i = startOffset - 1; i >= 0; i--) {
        const day = daysInPrevMonth - i;
        const prevM = viewMonth === 0 ? 11 : viewMonth - 1;
        const prevY = viewMonth === 0 ? viewYear - 1 : viewYear;
        cells.push({ iso: toISO(prevY, prevM, day), day, currentMonth: false });
    }

    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
        cells.push({ iso: toISO(viewYear, viewMonth, d), day: d, currentMonth: true });
    }

    // Next month head — fill to complete last row
    const remaining = cells.length % 7 === 0 ? 0 : 7 - (cells.length % 7);
    for (let d = 1; d <= remaining; d++) {
        const nextM = viewMonth === 11 ? 0 : viewMonth + 1;
        const nextY = viewMonth === 11 ? viewYear + 1 : viewYear;
        cells.push({ iso: toISO(nextY, nextM, d), day: d, currentMonth: false });
    }

    function handleDayClick(iso: string) {
        if (!dateHasEvent(iso, events)) return;
        onSelectDate(selectedDate === iso ? null : iso);
    }

    return (
        <div className="event-calendar">
            <div className="calendar-header">
                <button className="calendar-nav-btn" onClick={prevMonth} aria-label="Previous month">&#8249;</button>
                <span className="calendar-month-label">{MONTH_NAMES[viewMonth]} {viewYear}</span>
                <button className="calendar-nav-btn" onClick={nextMonth} aria-label="Next month">&#8250;</button>
            </div>
            <div className="calendar-grid">
                {DAYS_OF_WEEK.map((d) => (
                    <div key={d} className="calendar-weekday">{d}</div>
                ))}
                {cells.map(({ iso, day, currentMonth }) => {
                    const hasEvent = dateHasEvent(iso, events);
                    const isSelected = selectedDate === iso;
                    const isToday = iso === todayISO;
                    const classNames = [
                        'calendar-day',
                        currentMonth ? '' : 'calendar-day--other-month',
                        hasEvent ? 'calendar-day--event' : '',
                        isSelected ? 'calendar-day--selected' : '',
                        isToday ? 'calendar-day--today' : '',
                    ].filter(Boolean).join(' ');

                    return (
                        <div
                            key={iso}
                            className={classNames}
                            onClick={() => handleDayClick(iso)}
                            role={hasEvent ? 'button' : undefined}
                            tabIndex={hasEvent ? 0 : undefined}
                            onKeyDown={hasEvent ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleDayClick(iso); } : undefined}
                            aria-label={hasEvent ? `${day} — has events` : undefined}
                            aria-pressed={isSelected ? true : undefined}
                        >
                            <span className="calendar-day-number">{day}</span>
                            {hasEvent && <span className="calendar-dot" aria-hidden="true" />}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default EventCalendar;
